import { HOUR, MINUTE } from '../helpers';
import { Range } from '../model/ComponentDowntime';

export type TickUnit = 'number' | 'day' | 'hour' | 'minute';

export type TickOptions = Readonly<{
  unit: TickUnit;
  interval: number;
  labelInterval: number;
}>;

export type AxisProps = {
  start: Date | number;
  end: Date | number;
  formatTick?: (tick: Date | number) => string;
  width: number;
  height: number;
  tickOptions?: TickOptions | 'auto';
};

type AxisTick = {
  position: number;
  value: Date | number;
  labeled: boolean;
};

function findBestInterval(
  total: number,
  intervals: readonly number[],
  targetTickCount: number
) {
  return intervals.reduce((bestInterval, interval) => {
    const tickCount = total / interval;
    const bestTickCount = total / bestInterval;
    return Math.abs(tickCount - targetTickCount) <
      Math.abs(bestTickCount - targetTickCount)
      ? interval
      : bestInterval;
  }, intervals[0] ?? 0);
}

function heuristicDateTicks(
  start: number | Date,
  end: number | Date
): Omit<TickOptions, 'unit'> & { firstLabelOffset: number } {
  const duration = end.valueOf() - start.valueOf();

  const tickPresets = {
    day: [1, 2, 3, 4, 5, 6, 7, 14],
    hour: [1, 2, 3, 4, 6, 8, 12],
    minute: [1, 5, 10, 15, 30],
  } as const;

  const plainTickOptions = [
    ...tickPresets.day.map((opt) => opt * HOUR * 24),
    ...tickPresets.hour.map((opt) => opt * HOUR),
    ...tickPresets.minute.map((opt) => opt * MINUTE),
  ];

  const bestInterval = findBestInterval(duration, plainTickOptions, 25);
  const bestLabelInterval = findBestInterval(duration, plainTickOptions, 8);

  return {
    firstLabelOffset:
      bestInterval < 24 * HOUR && bestLabelInterval < 24 * HOUR
        ? new Date().getTimezoneOffset() * MINUTE
        : 0,
    interval: bestInterval,
    labelInterval: bestLabelInterval,
  };
}

function heuristicNumberTicks(
  start: number,
  end: number
): Omit<TickOptions, 'unit'> & { firstLabelOffset: number } {
  const duration = end - start;

  const readableNumbers = [1, 2, 2.5, 5];
  const plainTickOptions = [
    ...readableNumbers,
    ...readableNumbers.map((n) => n * 10),
    ...readableNumbers.map((n) => n * 100),
    ...readableNumbers.map((n) => n * 1000),
  ];

  const bestInterval = findBestInterval(duration, plainTickOptions, 25);
  const bestLabelInterval = findBestInterval(duration, plainTickOptions, 10);

  return {
    firstLabelOffset: 0,
    interval: bestInterval,
    labelInterval: bestLabelInterval,
  };
}

function generateTicks(
  range: Range,
  width: number,
  height: number,
  interval: number,
  labelInterval: number,
  firstLabelOffset: number
): AxisTick[] {
  const ticks: number[] = [];
  const numTicks = Math.ceil(range.length() / interval);
  const firstTick = range.start - (range.start % interval) + interval;
  const firstLabel =
    range.start - (range.start % labelInterval) + labelInterval;
  let labelOffset = (firstLabel - firstTick + firstLabelOffset) % labelInterval;
  if (labelOffset < 0) labelOffset += labelInterval;

  for (let i = 0; i <= numTicks; i++) {
    ticks.push(i * interval + firstTick);
  }
  return ticks.map((t, idx) => ({
    value: t,
    position: range.transformPoint(t, new Range(0, width)),
    labeled: (idx * interval) % labelInterval === labelOffset,
  }));
}

function formatDate(date: Date | number): string {
  return new Date(date).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(date: Date | number): string {
  return new Date(date).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

const Axis: React.FC<AxisProps> = ({
  start,
  end,
  formatTick,
  width,
  height,
  tickOptions = 'auto',
}) => {
  let interval: number;
  let labelInterval: number;
  let firstLabelOffset: number;
  if (tickOptions === 'auto') {
    if (start instanceof Date || end instanceof Date) {
      ({ interval, labelInterval, firstLabelOffset } = heuristicDateTicks(
        start,
        end
      ));
    } else {
      ({ interval, labelInterval, firstLabelOffset } = heuristicNumberTicks(
        start,
        end
      ));
    }
  } else {
    switch (tickOptions.unit) {
      case 'day':
        interval = tickOptions.interval * 24 * HOUR;
        labelInterval = tickOptions.labelInterval * 24 * HOUR;
        firstLabelOffset = new Date().getTimezoneOffset() * MINUTE;
        break;
      case 'hour':
        interval = tickOptions.interval * HOUR;
        labelInterval = tickOptions.labelInterval * HOUR;
        firstLabelOffset = new Date().getTimezoneOffset() * MINUTE;
        break;
      case 'minute':
        interval = tickOptions.interval * MINUTE;
        labelInterval = tickOptions.labelInterval * MINUTE;
        firstLabelOffset = new Date().getTimezoneOffset() * MINUTE;
        break;
      case 'number':
        ({ interval, labelInterval } = tickOptions);
        firstLabelOffset = 0;
        break;
    }
  }
  const ticks = generateTicks(
    new Range(start.valueOf(), end.valueOf()),
    width,
    height,
    interval,
    labelInterval,
    firstLabelOffset
  );

  let format: (v: Date | number) => string;
  if (formatTick) {
    format = formatTick;
  } else if (start instanceof Date || end instanceof Date) {
    if (end.valueOf() - start.valueOf() > 24 * HOUR) {
      format = formatDate;
    } else {
      format = formatTime;
    }
  } else {
    format = (v) => v.toString();
  }

  return (
    <g className="axes">
      <line
        x1="0"
        y1={height - 30}
        x2={width}
        y2={height - 30}
        stroke="black"
        strokeWidth="2"
      />
      {ticks.map((tick, index) => (
        <g key={index}>
          <line
            x1={`${tick.position}`}
            y1={height - 30}
            x2={`${tick.position}`}
            y2={0}
            stroke="black"
            strokeDasharray="2,5"
          />
          {tick.labeled ? (
            <>
              <line
                x1={`${tick.position}`}
                y1={height - 30}
                x2={`${tick.position}`}
                y2={height - 40}
                stroke="black"
              />
              <text
                x={`${tick.position}`}
                y={height - 10}
                textAnchor="middle"
                fontSize="10"
              >
                {format(tick.value)}
              </text>
            </>
          ) : null}
        </g>
      ))}
    </g>
  );
};

export { Axis };
